import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, KeyRound } from "lucide-react";
import memberApi, { formatErr } from "../../lib/memberApi";
import { PortalNav } from "../../components/portal/PortalNav";
import { toast } from "sonner";

const LOOKUP_LABEL = {
  gtg_pack_onetime: "Privacy & Disappearance Pack",
  gtg_optout_directory: "Data Broker Opt-Out Directory",
};

export default function Account() {
  const [acct, setAcct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await memberApi.get("/member/account");
        setAcct(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const changePw = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await memberApi.post("/member/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      setCur(""); setNext("");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Could not update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] noise-overlay text-white">
      <PortalNav />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {loading ? (
          <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-accent" size={28} /></div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent mb-3">// Account</p>
            <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mb-8">My Account</h1>

            <div className="border border-white/10 bg-[#0a0a0a] p-6 mb-6" data-testid="account-details">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">Email</span>
                  <span className="font-body text-sm text-white/90 break-all">{acct?.email}</span>
                </div>
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">Access level</span>
                  <span className="flex items-center gap-2 font-body text-sm text-white/90">
                    <ShieldCheck size={15} className="text-accent" /> {acct?.access}
                  </span>
                </div>
              </div>
            </div>

            <div className="border border-white/10 bg-[#0a0a0a] p-6 mb-6" data-testid="account-orders">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-4">Purchase history</span>
              {(acct?.orders || []).length === 0 ? (
                <p className="font-body text-sm text-white/40">No purchases on record.</p>
              ) : (
                <ul className="divide-y divide-white/10">
                  {acct.orders.map((o) => (
                    <li key={o.session_id} className="py-3 flex items-center justify-between">
                      <div>
                        <span className="font-body text-sm text-white/85">{LOOKUP_LABEL[o.lookup_key] || o.lookup_key}</span>
                        <span className="block font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">
                          {o.created_at ? new Date(o.created_at).toLocaleDateString() : ""}
                        </span>
                      </div>
                      <span className="font-mono text-sm text-white/70">${((o.amount || 0) / 100).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <form onSubmit={changePw} className="border border-white/10 bg-[#0a0a0a] p-6" data-testid="account-change-password">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 flex items-center gap-2 mb-4"><KeyRound size={13} /> Change password</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <input
                  data-testid="account-current-password"
                  type="password" required value={cur} onChange={(e) => setCur(e.target.value)}
                  placeholder="Current password"
                  className="w-full bg-black/40 border border-white/15 px-4 py-3 font-mono text-sm text-white focus:outline-none focus:border-accent transition-colors"
                />
                <input
                  data-testid="account-new-password"
                  type="password" required minLength={6} value={next} onChange={(e) => setNext(e.target.value)}
                  placeholder="New password (min 6)"
                  className="w-full bg-black/40 border border-white/15 px-4 py-3 font-mono text-sm text-white focus:outline-none focus:border-accent transition-colors"
                />
              </div>
              <button
                data-testid="account-save-password"
                type="submit" disabled={saving}
                className="inline-flex items-center gap-2 bg-accent text-white font-mono uppercase tracking-[0.15em] text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-70"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : "Update Password"}
              </button>
            </form>
          </motion.div>
        )}
      </main>
    </div>
  );
}
